// Standalone SEPA mandate setup page — /sepa?pid=P12345
//
// Runs the same GoCardless Drop-in as Step 11, but outside the questionnaire
// flow, so mandate setup can be tested (and later sent to patients as a direct
// link) without walking through the whole onboarding. No submission is written
// here — the invoice system's webhooks own the mandate lifecycle.
//
// Org safety: orgs with skipSepa (ES/IT) render the unavailable state; the
// backend additionally rejects when sepa_dropin is 'off' for the org.
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGoCardlessDropin } from '@gocardless/react-dropin'
import { Logo } from '../components/Logo'
import { Input } from '../components/Input'
import { Button } from '../components/Button'
import { useTenant } from '../context/TenantContext'
import { sepaService } from '../services/api'
import { logFlowEvent } from '../services/flowEventService'
import { logger } from '../utils/logger'
import { toast } from 'sonner'
import { CheckCircle2, ShieldCheck } from 'lucide-react'
import { LegalFooter } from '../components/LegalFooter'

type PageState = 'idle' | 'loading' | 'open' | 'success' | 'already_active' | 'abandoned' | 'unavailable'

export function SepaSetupPage() {
  const { t } = useTranslation()
  const { tenant } = useTenant()

  const [pid, setPid] = useState(() =>
    (new URLSearchParams(window.location.search).get('pid') || '').toUpperCase()
  )
  const [state, setState] = useState<PageState>('idle')
  const [flow, setFlow] = useState<{ id: string; env: 'live' | 'sandbox' } | null>(null)
  const openedRef = useRef(false)

  const pidValid = /^P\d+$/.test(pid)
  const sepaAvailable = tenant ? !tenant.skipSepa : true

  const { open: openDropin, ready: dropinReady } = useGoCardlessDropin({
    billingRequestFlowID: flow?.id ?? '',
    environment: flow?.env ?? 'live',
    onSuccess: (billingRequest) => {
      logger.log('[SEPA Page] success, billing request:', billingRequest.id)
      logFlowEvent(pid, 'sepa_page', 'step_completed')
      setFlow(null)
      setState('success')
    },
    onExit: (error) => {
      openedRef.current = false
      setFlow(null)
      if (error) {
        logger.error('[SEPA Page] exit with error:', error)
        logFlowEvent(pid, 'sepa_page', 'error')
        toast.error(t('sepaDropin.error'))
      }
      setState('abandoned')
    },
  })

  useEffect(() => {
    if (flow && dropinReady && !openedRef.current) {
      openedRef.current = true
      logFlowEvent(pid, 'sepa_page', 'step_entered')
      setState('open')
      openDropin()
    }
  }, [flow, dropinReady, openDropin, pid])

  const start = async () => {
    if (!pidValid) return
    setState('loading')
    try {
      const result = await sepaService.getDropinFlow(pid)

      if (result.status === 'already_active') {
        setState('already_active')
        return
      }
      if (result.available && result.status === 'created' && result.billingRequestFlowId) {
        setFlow({ id: result.billingRequestFlowId, env: result.environment === 'sandbox' ? 'sandbox' : 'live' })
        return
      }

      logger.warn('[SEPA Page] drop-in unavailable:', result.reason)
      // Fallback to the hosted GoCardless page when we got one
      if (result.authorizationUrl) {
        window.location.href = result.authorizationUrl
        return
      }
      if (tenant?.sepaRedirectUrl) {
        window.location.href = `${tenant.sepaRedirectUrl}?pid=${encodeURIComponent(pid)}`
        return
      }
      setState('unavailable')
    } catch (err) {
      logger.error('[SEPA Page] flow fetch failed:', err)
      toast.error(t('sepaDropin.error'))
      setState('idle')
    }
  }

  return (
    <div className="min-h-screen bg-muted flex flex-col">
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-card shadow-sm p-8 max-w-md w-full">
          <div className="flex justify-center mb-6">
            <Logo size={100} />
          </div>

          {!sepaAvailable ? (
            <p className="text-body text-muted-foreground text-center">{t('sepaPage.notAvailable')}</p>
          ) : state === 'success' ? (
            <div className="text-center">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <p className="text-body text-foreground">{t('sepaDropin.success')}</p>
            </div>
          ) : state === 'already_active' ? (
            <div className="text-center">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <p className="text-body text-foreground">{t('sepaDropin.alreadyActive')}</p>
            </div>
          ) : state === 'unavailable' ? (
            <p className="text-body text-muted-foreground text-center">{t('sepaDropin.unavailable')}</p>
          ) : (
            <>
              <h1 className="text-h2 text-foreground mb-2 text-center">{t('sepaPage.title')}</h1>
              <p className="text-body text-muted-foreground mb-6 text-center">{t('sepaPage.subtitle')}</p>

              <div className="space-y-4">
                <Input
                  id="pid"
                  type="text"
                  label={t('step1.patientNumber')}
                  placeholder={t('step1.placeholder')}
                  value={pid}
                  onChange={(e) => setPid(e.target.value.toUpperCase())}
                  error={pid && !pidValid ? t('step1.validation') : undefined}
                />

                {state === 'abandoned' && (
                  <p className="text-small text-muted-foreground">{t('sepaDropin.abandoned')}</p>
                )}

                <Button
                  onClick={start}
                  disabled={!pidValid || state === 'loading' || state === 'open'}
                  className="w-full"
                >
                  {state === 'loading' || state === 'open'
                    ? t('common.loading')
                    : state === 'abandoned'
                      ? t('sepaDropin.retry')
                      : t('sepaPage.start')}
                </Button>

                <p className="text-small text-muted-foreground flex items-start gap-2">
                  <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{t('sepaDropin.trustNote')}</span>
                </p>
              </div>
            </>
          )}
        </div>
      </main>
      <LegalFooter />
    </div>
  )
}
